import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { Router } from '@angular/router';

import { AppComponent } from './app.component';
import { HydraDebugComponent } from './shared/components/hydra-debug/hydra-debug.component';
import { AppRoutingModule } from './app-routing.module';

import { Auth0HttpInterceptor } from './shared/helpers/auth0.http.interceptor';
import { ErrorInterceptor } from './shared/helpers/error.interceptor';
import { CacheInterceptor } from './shared/helpers/cache.interceptor';

import { environment } from '../environments/environment';
import { ScreenService } from './shared/services/screen.service';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';
import { PosthogService } from './shared/services/posthog.service';
import { MedigiFooterModule } from './layouts/footer/medigi/footer.component';
import { HeaderModule } from './layouts/header/header.component';
import { SideNavOuterToolbarModule } from './layouts/side-nav-outer-toolbar/side-nav-outer-toolbar.component';
import { NavModule } from './layouts/nav/medigi-nav/nav.component';
import { DatePipe } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import * as Sentry from '@sentry/angular-ivy';
import { MedshortsNavModule } from './layouts/nav/medshorts-nav/nav.component';
import { OutsourcingNavModule } from './layouts/nav/outsourcing-nav/nav.component';
import { MedshortsFooterModule } from './layouts/footer/medshorts/footer.component';
import { OutsourcingFooterModule } from './layouts/footer/outsourcing/footer.component';
import { JwtModule } from './pages/jwt/jwt.module';
import { ApiCacheInterceptor } from './shared/helpers/api.cache.interceptor';
import { DirectModule } from './shared/components/direct/direct.module';
import { ApiDupeInterceptor } from './shared/helpers/api.dupe.interceptor';
import { FeatureFlagsService } from './shared/services/featureFlags.service';
import { PreRegTermsModule } from './shared/components/pre-reg-terms/pre-reg-terms.component';
import { FacilityHeaderModule } from './shared/components/facility-header/facility-header.component';
import { PastDueBannerModule } from './shared/components/past-due-banner/past-due-banner.component';
import { EmailConfirmationBannerModule } from './shared/components/email-confirmation-banner/email-confirmation-banner.component';
import { OrderingBlackoutBannerModule } from './shared/components/ordering-blackout-banner/ordering-blackout-banner.component';
import { MarkdownModule } from 'ngx-markdown';

export function initFeatureFlags(featureFlagsService: FeatureFlagsService) {
  return () => featureFlagsService.getFeatureFlags();
}

// The ErrorInterceptor rethrows server error bodies as bare strings; when one
// escapes unhandled it reaches this global handler with no type and no stack,
// so Sentry groups everything as one useless <unknown> issue. Wrap strings in
// a real Error at THIS boundary only — handled component callbacks never reach
// the global handler, so they keep receiving the raw value they expect.
const sentryErrorHandler = Sentry.createErrorHandler({ showDialog: false });
class StringWrappingErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (typeof error === 'string') {
      // Fingerprint by message: every wrapped string shares this wrap-point's
      // stack, which otherwise collapses distinct errors into one Sentry issue (MDG-69).
      const wrapped = new Error(error);
      Sentry.withScope(scope => {
        scope.setFingerprint(['string-error', error as string]);
        sentryErrorHandler.handleError(wrapped);
      });
      return;
    }
    sentryErrorHandler.handleError(error);
  }
}

if (environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: `${environment.env_name}-${(environment as any).supplierConfig?.clientNameSpace ?? 'unknown'}`,
    autoSessionTracking: true,
    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.browserTracingIntegration(),
      // Registers the Replay integration,
      // which automatically captures Session Replays
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [environment.api_url_v2],
        networkRequestHeaders: ['SourceDomain'],
      }),
    ],

    // No one actively watches blanket frontend perf; sample a small slice to stay
    // within the Sentry Performance quota (was 1.0 = 100%). Add a tracesSampler
    // for specific routes later if a flow needs closer monitoring.
    tracesSampleRate: 0.05,

    // Session replay was 4x over quota (2k/500). Stop blanket session replay;
    // keep 100% replay on error (low volume, high value — the case we care about).
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,

    // Deliberately drop HTTP status-0 network noise: 'Unknown Error' is the
    // browser's statusText for status 0 (connection drop, timeout, ad-blocker,
    // navigate-away mid-request) — client-network weather, not an app bug.
    // It fed MEDIGI-FRONTEND-4 (~2.7k events/30d since 2023) and its
    // uncaught-promise variants. Anchored on the exact statusText so real
    // errors that merely mention it are kept.
    beforeSend(event) {
      const message =
        event.message ||
        event.exception?.values?.[event.exception.values.length - 1]?.value ||
        '';
      if (/(^|: )Unknown Error$/.test(message)) {
        return null;
      }
      return event;
    },
  });
}

@NgModule({
  declarations: [AppComponent, HydraDebugComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    // for HttpClient use:
    LoadingBarHttpClientModule,
    // for Router use:
    LoadingBarRouterModule,
    // for Core use:
    LoadingBarModule,

    MedigiFooterModule,
    MedshortsFooterModule,
    OutsourcingFooterModule,
    JwtModule,
    HeaderModule,
    NavModule,
    MedshortsNavModule,
    OutsourcingNavModule,
    SideNavOuterToolbarModule,
    DirectModule,
    PreRegTermsModule,
    DxButtonModule,
    FacilityHeaderModule,
    PastDueBannerModule,
    EmailConfirmationBannerModule,
    OrderingBlackoutBannerModule,
    MarkdownModule.forRoot(),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initFeatureFlags,
      deps: [FeatureFlagsService],
      multi: true,
    },
    ScreenService,
    GoogleAnalyticsService,
    PosthogService, // PostHog autocapture analytics

    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: Auth0HttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiDupeInterceptor, multi: true },

    DatePipe,

    {
      provide: ErrorHandler,
      useClass: StringWrappingErrorHandler,
    },

    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ],
  exports: [],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(trace: Sentry.TraceService) {}
}
